import { useElements, useStripe } from '@stripe/react-stripe-js';
import { skipToken, useQuery } from '@tanstack/react-query';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { CartCalculationOrderResponse } from '~/api/Catalog';
import { ETAPaymentMethod } from '~/api/ETA';
import { PaymentSystem } from '~/api/Order';
import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import { ModalType } from '~/components/Modal/interface';
import Panel from '~/components/Panel/Panel';
import Price from '~/components/Price';
import Promocode2 from '~/components/Promocode2/Promocode2';
import { UI_BOTTOM_COMPONENTS } from '~/constants/variables';
import { useCheckoutButton } from '~/hooks/useCheckoutButton';
import { useGlobal } from '~/hooks/useGlobal';
import { useHeaderSize } from '~/hooks/useHeaderSize';
import { useModal } from '~/hooks/useModal';
import { useSticky } from '~/hooks/useSticky';
import { useIsTablet } from '~/pages/Order/hooks';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import {
  cartStore,
  checkoutStore,
  mainStore,
  orderStore,
  userStore,
} from '~/stores';
import { awsPaymentStore } from '~/stores/AwsPaymentStore';
import type { RequestETAResponse } from '~/stores/OrderStore';
import { formatPriceWithCurrency } from '~/utils/formaters';

import BottomNavigationCheckoutBarV2 from '../Root/BottomNavigationBar/BottomNavigationCheckoutBarV2';

import styles from './CheckoutContent.module.scss';
import stylesCd from './CheckoutContentCd.module.scss';
import CheckoutContentMobileCD from './CheckoutContentMobileCD';
import DeliveryAddressRestriction from './components/DeliveryAddressRestriction';
import DeliveryTime from './components/DeliveryTime';
import MapStatic from './components/MapStatic/MapStatic';
import PaymentPanel from './components/PaymentPanel/PaymentPanel';
import Total from './components/Total/Total';

const CheckoutContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { isMobile } = useGlobal();
  const stripe = useStripe();
  const elements = useElements();
  const [, setDeliveryPrices] = useState<RequestETAResponse | null>(null);
  const isTablet = useIsTablet();
  const innerAsideRef = useRef<HTMLDivElement>(null);
  const outerAsideRef = useRef<HTMLDivElement>(null);
  const { headerHeight } = useHeaderSize();
  const { isDisabled, onClick, text, description, isDisabledForm } =
    useCheckoutButton();
  const isLoadingConfirm =
    checkoutStore.isLoading ||
    orderStore.isLoading ||
    awsPaymentStore.isIframeLoading;

  useSticky({
    topSpacing: headerHeight,
    outerRef: outerAsideRef,
    innerRef: innerAsideRef,
  });

  const loyaltyPointsWorkWithPromocode =
    userStore.loyaltyPointsStore.workWithPromocode;
  const loyaltyPointsUse = userStore.loyaltyPointsStore.useLoyaltyPoints;
  const promocodeUse = cartStore.promotionStore.promocode.success;

  const minimalOrderValueError =
    cartStore.totalCartPrice.minimal_order_value_error?.length &&
    cartStore.totalCartPrice.minimal_order_value_error[0] ? (
      <>
        {t('errors:minCartError', {
          amount: formatPriceWithCurrency(
            cartStore.totalCartPrice.minimal_order_value_error[0]
              ?.minimalOrderValue / 100,
          ),
        })}
        <span className={stylesCd.checkoutConfirmButtonDescription}>
          {t('errors:minCartErrorDetails', {
            amount: formatPriceWithCurrency(
              (cartStore.totalCartPrice.minimal_order_value_error[0]
                ?.minimalOrderValue -
                cartStore.totalCartPrice.minimal_order_value_error[0]
                  ?.orderPriceWithoutDelivery) /
                100,
            ),
          })}
        </span>
      </>
    ) : null;

  const hasExpressDelivery = checkoutStore.deliveries?.some(
    (delivery) => !delivery.slotDeliveryDetails,
  );

  const totalDeliveryFee = cartStore.deliveriesOrders.reduce(
    (acc, { is_delivery_free, delivery_info: { paid_price } }) => {
      if (is_delivery_free) {
        return acc;
      }
      return acc + paid_price;
    },
    0,
  );

  const isDeliveryMethodSelectionAvailable =
    company.deliveryMethodSelectionAvailable &&
    checkoutStore.availableDeliveryMethods.length > 1;

  const isPromoLoading = cartStore.promotionStore.applyPromocodeMeta.isLoading;

  useEffect(() => {
    if (checkoutStore.activePaymentSystem !== PaymentSystem.stripe) return;

    if (!stripe || !elements) return;

    checkoutStore.requestPayments().finally(() => {
      if (
        checkoutStore.isRefreshed &&
        !checkoutStore.isLoading &&
        !isDisabledForm() &&
        !minimalOrderValueError
      ) {
        checkoutStore.orderCheckout(stripe, elements);
      }
    });
  }, [checkoutStore.activePaymentSystem]);

  useEffect(() => {
    if (checkoutStore.orderId) {
      return;
    }

    if (
      cartStore.promocode.success &&
      mainStore.isZero(cartStore.totalCartPrice.promocodeDiscount) &&
      cartStore.promocode.coupon?.minimumPurchase
    ) {
      mainStore.setIsInvalidPromocodePopover(true);
    }
  }, [cartStore.totalCartPrice]);

  useEffect(() => {
    if (checkoutStore.orderId || cartStore.cart.length) {
      return;
    }
    navigate('/cart', { replace: true });
  }, [cartStore.cart.length]);

  useEffect(() => {
    checkoutStore.setAddressVal(userStore.deliveryAddress?.address1 || '');
    checkoutStore.setAddress2Val(userStore.deliveryAddress?.address2 || '');
    checkoutStore.setAddressFloorVal(
      userStore.deliveryAddress?.addressFloor || '',
    );
    checkoutStore.setAddressBldVal(userStore.deliveryAddress?.addressBld || '');
    checkoutStore.setAddressAptVal(userStore.deliveryAddress?.addressApt || '');
  }, [userStore.deliveryAddress, userStore.deliveryAddress?.address2]);

  useEffect(() => {
    if (!checkoutStore.orderId) {
      return;
    }

    if (
      checkoutStore.activePaymentSystem === PaymentSystem.octo &&
      checkoutStore.octoPaymentLink
    ) {
      return;
    }

    if (
      checkoutStore.activePaymentSystem === PaymentSystem.pvit &&
      checkoutStore.activePaymentMethod &&
      checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash
    ) {
      navigate(
        `/order-payment/${checkoutStore.orderId}?payment_method=${checkoutStore.activePaymentMethod}`,
        { replace: true },
      );
      return;
    }

    navigate(`/order/${checkoutStore.orderId}`, {
      replace: true,
      state: { linkBack: '/' },
    });
  }, [checkoutStore.orderId]);

  const handleOctoPaymentComplete = useCallback(
    (orderId: string) => {
      if (mainStore.isRN) {
        mainStore.sendToRN('closeInAppBrowser');
      }
      checkoutStore.octoPaymentLink = null;
      checkoutStore.onPaymentSuccess(orderId);
      navigate(`/order/${orderId}`, {
        replace: true,
        state: { linkBack: '/' },
      });
    },
    [navigate],
  );

  const handleCloseOctoIframe = useCallback(async () => {
    if (mainStore.isRN) mainStore.sendToRN('closeInAppBrowser');
    checkoutStore.octoPaymentLink = null;

    if (!checkoutStore.orderId) return;

    const order = await orderStore.requestOrder(checkoutStore.orderId);
    if (order && order.status !== 'created' && order.status !== 'reserved') {
      handleOctoPaymentComplete(checkoutStore.orderId);
    } else {
      checkoutStore.setOrderId('');
    }
  }, [handleOctoPaymentComplete]);

  const isOctoPollingEnabled =
    !!checkoutStore.orderId &&
    checkoutStore.activePaymentSystem === PaymentSystem.octo;

  const { data: octoOrder } = useQuery({
    queryKey: ['octo-payment', checkoutStore.orderId, isOctoPollingEnabled],
    queryFn: isOctoPollingEnabled
      ? () => orderStore.requestOrder(checkoutStore.orderId)
      : skipToken,
    refetchInterval: (query) => {
      const { data } = query.state;
      return data && data.status !== 'created' && data.status !== 'reserved'
        ? false
        : 5000;
    },
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    if (
      octoOrder &&
      octoOrder.status !== 'created' &&
      octoOrder.status !== 'reserved'
    ) {
      handleOctoPaymentComplete(checkoutStore.orderId);
    }
  }, [octoOrder, handleOctoPaymentComplete]);

  useEffect(() => {
    setDeliveryPrices(checkoutStore.deliveryPrices);
  }, [checkoutStore.isUpdatedDeliveryPopover]);

  useEffect(() => {
    if (userStore.deliveryAddress?.comment) {
      checkoutStore.setCommentVal(userStore.deliveryAddress?.comment || '');
    }
  }, [userStore.deliveryAddress?.addressId]);

  useEffect(() => {
    if (userStore.deliveryAddress?.instructions) {
      checkoutStore.setInstructionList(
        Object.assign({}, userStore.deliveryAddress?.instructions),
      );
    }
  }, [userStore.deliveryAddress?.instructions]);

  const handleSelectDateModalCD = (
    index: number,
    order: CartCalculationOrderResponse,
  ) => {
    const delivery = checkoutStore.deliveries?.[index];

    if (!delivery) {
      return;
    }

    analyticsService.track(AnalyticsEvent.DELIVERY_METHOD_SELECT_TIME, {});

    checkoutStore.slotSelectionModalStore.setSelectedSlot(delivery, order);
    openModal(ModalType.SelectDateCD);
  };

  const openDeliveryModal = () => {
    openModal(ModalType.DeliveryInfo, 'checkout');
  };

  return (
    <>
      {isTablet ? (
        <CheckoutContentMobileCD
          handleSelectDateModal={handleSelectDateModalCD}
          hasExpressDelivery={hasExpressDelivery}
          totalDeliveryFee={totalDeliveryFee}
          isRestrictedPayment={isDisabledForm() || !!minimalOrderValueError}
          isDeliveryMethodSelectionAvailable={
            isDeliveryMethodSelectionAvailable
          }
          isPromoLoading={isPromoLoading}
          openDeliveryModal={openDeliveryModal}
          promocodeUse={promocodeUse}
        />
      ) : (
        <div
          className={classes([
            'checkout desktop',
            company.newDesignSystemEnabled && stylesCd.newVersion,
          ])}
          data-company={company.name}
        >
          <div className="content-layout">
            <div className={stylesCd.panelTitleContainerCd}>
              <p
                className={classes([
                  styles.panelTitle,
                  styles.deliverySelectionTitle,
                ])}
              >
                {t('checkout')}
              </p>
            </div>
            <div className={styles.checkoutContent}>
              <div className={styles.checkoutContentInfo}>
                <MapStatic />
                {company.showCheckoutAddressRestrictionBanner && (
                  <DeliveryAddressRestriction />
                )}
                <DeliveryTime handleSelectDateModal={handleSelectDateModalCD} />
              </div>
              <div className={styles.checkoutPayment}>
                <PaymentPanel />
                <Panel icon="promocode">
                  <Promocode2
                    disabled={
                      !loyaltyPointsWorkWithPromocode && loyaltyPointsUse
                    }
                  />
                </Panel>
                <Total />
              </div>
            </div>
          </div>
        </div>
      )}

      {(company.useAwsPaymentMethod &&
        checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash) ||
      company.newDesignSystemEnabled
        ? null
        : cartStore.cart.length > 0 && (
            <div
              className="mobile-bottom-button-container"
              id={UI_BOTTOM_COMPONENTS.CTA}
            >
              <button
                data-button="confirm-order"
                className={classes([
                  'button _primary',
                  description?.length > 0 || minimalOrderValueError
                    ? '_big'
                    : '_med',
                  stylesCd.checkoutConfirmButton,
                  (description?.length > 0 || minimalOrderValueError) &&
                    stylesCd.checkoutConfirmButtonColumn,
                ])}
                disabled={isDisabled || !!minimalOrderValueError}
                onClick={onClick}
              >
                {!minimalOrderValueError ? text : minimalOrderValueError}
                {!minimalOrderValueError && (
                  <>
                    {isLoadingConfirm ? (
                      <span className="spinner _white" />
                    ) : description?.length > 0 ? (
                      <span
                        className={stylesCd.checkoutConfirmButtonDescription}
                      >
                        {description}
                      </span>
                    ) : (
                      <Price
                        price={cartStore.finalPrice}
                        classNameCurrentPrice={
                          stylesCd.checkoutConfirmButtonPrice
                        }
                      />
                    )}
                  </>
                )}
              </button>
            </div>
          )}
      {isMobile && company.newDesignSystemEnabled ? (
        <BottomNavigationCheckoutBarV2 />
      ) : null}

      {checkoutStore.octoPaymentLink && !mainStore.isRN && (
        <div className={stylesCd.octoPaymentOverlay}>
          <div className={stylesCd.octoPaymentIframeContainer}>
            <button
              type="button"
              className={stylesCd.octoPaymentCloseButton}
              onClick={handleCloseOctoIframe}
            >
              <Icon type="close" size={24} />
            </button>
            <iframe
              src={checkoutStore.octoPaymentLink}
              title="Octo Payment"
              allow="payment"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default observer(CheckoutContent);
